import AXIOS from "@/axios";
import { getAllCoursesService } from "@/service/course.service";
import { removeEmptyProps } from "@/utils/constants";
import moment from "moment";
import type { APIResponse } from "@/types";
import type { ClassFormSchemaValues } from "../schemas/classForm.schema";
import { CLASSES_PER_PAGE } from "../constants/class.constants";
import type {
  AdminClass,
  ClassExportFormat,
  ClassExportResult,
  ClassListQuery,
  ClassListResult,
  ClassMutationResult,
  LinkableGrade,
} from "../types/class.types";
import type { ClassApi } from "./classApi.interface";
import type { AdminClassApiItem, AdminClassListResponse } from "./adminClass.types";
import {
  formToCreatePayload,
  formToUpdatePayload,
  mapApiItemToAdminClass,
} from "./adminClass.mappers";

function formatQueryDates(query: ClassListQuery): Record<string, unknown> {
  const params: Record<string, unknown> = {
    page: query.page ?? 1,
    per_page: query.pageSize ?? CLASSES_PER_PAGE,
    search: query.search,
    sortBy: query.sortBy,
    sortOrder: query.sortOrder,
  };
  if (query.fromDate && query.toDate) {
    params.fromDate = moment(new Date(query.fromDate).toISOString()).format(
      "DD-MM-YYYY"
    );
    params.toDate = moment(new Date(query.toDate).toISOString()).format(
      "DD-MM-YYYY"
    );
  }
  return params;
}

export const classApiHttp: ClassApi = {
  async listClasses(query: ClassListQuery): Promise<ClassListResult> {
    const response = await AXIOS.get<AdminClassListResponse>(
      "/class/api/v1/list",
      { params: removeEmptyProps(formatQueryDates(query)) }
    );

    const body = response.data;
    return {
      data: (body.data ?? []).map(mapApiItemToAdminClass),
      totalItems: body.pageInfo?.total_count ?? 0,
      totalPages: body.pageInfo?.total_pages ?? 1,
      currentPage: body.pageInfo?.current_page ?? 1,
    };
  },

  async getClassById(id: string): Promise<AdminClass | null> {
    const response = await AXIOS.get<APIResponse<AdminClassApiItem>>(
      `/class/api/v1/view/${id}`
    );
    const data = response.data?.data;
    if (!data) return null;
    return mapApiItemToAdminClass(data);
  },

  async createClass(values: ClassFormSchemaValues): Promise<ClassMutationResult> {
    const payload = formToCreatePayload(values);
    const response = await AXIOS.post<APIResponse<Record<string, unknown>>>(
      "/class/api/v1/add",
      payload,
      { skipGlobalErrorToast: true }
    );
    const data = response.data?.data;
    const newId = typeof data?.id === "string" ? data.id : "";
    if (!newId) {
      throw new Error("Create class returned no id");
    }
    const loaded = await this.getClassById(newId);
    if (!loaded) {
      throw new Error("Could not load class after create");
    }
    return {
      class: loaded,
      message: response.data?.message?.trim() ?? "",
    };
  },

  async updateClass(
    id: string,
    values: ClassFormSchemaValues
  ): Promise<ClassMutationResult> {
    const existing = await this.getClassById(id);
    const payload = formToUpdatePayload(values, existing?.isActive ?? true);
    const response = await AXIOS.patch<APIResponse<AdminClassApiItem>>(
      `/class/api/v1/update/${id}`,
      payload,
      { skipGlobalErrorToast: true }
    );
    const data = response.data?.data;
    if (!data) {
      throw new Error("Update class returned no data");
    }
    return {
      class: mapApiItemToAdminClass(data),
      message: response.data?.message?.trim() ?? "",
    };
  },

  async toggleClassActive(
    id: string,
    isActive: boolean
  ): Promise<ClassMutationResult> {
    const response = await AXIOS.patch<APIResponse<AdminClassApiItem>>(
      `/class/api/v1/update/${id}`,
      { is_active: isActive },
      { skipGlobalErrorToast: true }
    );
    const data = response.data?.data;
    if (!data) {
      throw new Error("Update class status returned no data");
    }
    return {
      class: mapApiItemToAdminClass(data),
      message: response.data?.message?.trim() ?? "",
    };
  },

  async exportClasses(
    format: ClassExportFormat,
    query: Pick<ClassListQuery, "search" | "fromDate" | "toDate">
  ): Promise<ClassExportResult> {
    const response = await AXIOS.get(`/class/api/v1/dwnld/${format}`, {
      params: removeEmptyProps(formatQueryDates(query)),
      responseType: "blob",
      skipGlobalErrorToast: true,
    });
    const headerMessage = response.headers?.message;
    return {
      data: response.data as Blob,
      message:
        typeof headerMessage === "string" ? headerMessage.trim() : "",
    };
  },

  async listLinkableGrades(): Promise<LinkableGrade[]> {
    const grades: LinkableGrade[] = [];
    let page = 1;
    const pageSize = 100;

    while (page <= 50) {
      const response = await getAllCoursesService({ page, pageSize });
      const rows = response.data?.data ?? [];
      for (const row of rows) {
        grades.push({ id: row.id, name: row.grade ?? row.id });
      }
      const totalPages = response.data?.pageInfo?.total_pages ?? 1;
      if (page >= totalPages) break;
      page += 1;
    }

    return grades;
  },
};
